import {useState} from "react";
import {Filters, SubmitAction} from "./Filters";
import {Call, CallsInterval} from "./types";
import {Duration} from "../widgets";
import {downloadRecords} from "@/components/DownloadRecordsCsv";
import {getRecordListInterval} from "@/components/IntervalWidget";
import {RecordList} from "@/components/RecordList";
import {FormattedMessage, useIntl, defineMessages, defineMessage} from "react-intl";
import {DateTime} from "luxon";
import {KeyString} from "@/tools";
import RecordingPlayer from "@/components/call-list/RecordingPlayer";
import {CallListQueryVariables, useCallListLazyQuery} from "./queries.generated";
import RecordingIdCopier from "./RecordingIdCopier";
import {RecordingStore, SegmentType} from "@/api";
import {Call as APICall} from "@/api";

type CSVField = Extract<keyof Call, "localNumber" | "remoteNumber" | "direction" | "callTime" | "duration">;
const csvFields: CSVField[] = ["localNumber", "remoteNumber", "direction", "callTime", "duration"] as const;

function stripDomain(number: string, domain: string): string {
    if (number.endsWith(domain)) return number.substring(0, number.length - domain.length - 1);
    return number;
}

const timeGenerator = (call: Call, field: KeyString<Call>) => {
    return DateTime.fromISO(call[field]).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
};

// Static messages for call directions (keys based on backend-provided short codes)
const CALL_DIRECTION_MESSAGES = defineMessages({
    c: {id: "call_list.directions.c", defaultMessage: "Conference"},
    d: {id: "call_list.directions.d", defaultMessage: "Domain"},
    i: {id: "call_list.directions.i", defaultMessage: "Incoming"},
    m: {id: "call_list.directions.m", defaultMessage: "Private gateway"},
    o: {id: "call_list.directions.o", defaultMessage: "External"},
    s: {id: "call_list.directions.s", defaultMessage: "Service"},
    x: {id: "call_list.directions.x", defaultMessage: "External"},
});

// Segment type translations
const CALL_SEGMENT_TYPE_MESSAGES = defineMessages<SegmentType>({
    HOLD: {id: "call_list.segmentTypes.HOLD", defaultMessage: "Hold"},
    HOLD_MUSIC: {id: "call_list.segmentTypes.HOLD_MUSIC", defaultMessage: "Hold music"},
    NORMAL: {id: "call_list.segmentTypes.NORMAL", defaultMessage: "Normal"},
    REROUTE: {id: "call_list.segmentTypes.REROUTE", defaultMessage: "Reroute"},
    TRANSFER: {id: "call_list.segmentTypes.TRANSFER", defaultMessage: "Transfer"},
    TRANSFER_BY_BOTH: {id: "call_list.segmentTypes.TRANSFER_BY_BOTH", defaultMessage: "Transfer by both"},
    TRANSFER_BY_CALLEE: {id: "call_list.segmentTypes.TRANSFER_BY_CALLEE", defaultMessage: "Transfer by callee"},
    TRANSFER_BY_CALLER: {id: "call_list.segmentTypes.TRANSFER_BY_CALLER", defaultMessage: "Transfer by caller"},
});

const CALL_LIST_CSV_HEADER_MESSAGES = defineMessages<CSVField>({
    localNumber: {id: "call_list.localNumber", defaultMessage: "Local number"},
    remoteNumber: {id: "call_list.remoteNumber", defaultMessage: "Remote number"},
    direction: {id: "call_list.direction", defaultMessage: "Direction"},
    callTime: {id: "call_list.callTime", defaultMessage: "Time"},
    duration: {id: "call_list.duration", defaultMessage: "Duration"},
});

const recordingIdGenerator = (call: APICall) => {
    if (call.recordingId && call.recordingStore && call.connected) {
        if (call.recordingStore == RecordingStore.Internal) return <RecordingPlayer recordingId={call.recordingId} />;
        return <RecordingIdCopier recordingId={call.recordingId} />;
    }

    return null;
};

export const fieldContentGenerators: {[K in keyof Omit<APICall, "__typename">]?: (call: APICall) => any} = {
    onBehalfOf: (call: APICall) => call.onBehalfOf,
    localNumber: (call: APICall) => call.localNumber,
    localE164: (call: APICall) => call.localE164,
    remoteNumber: (call: APICall) => stripDomain(call.remoteNumber, call.localDomain),
    remoteE164: (call: APICall) => call.remoteE164,
    direction: (call: APICall) => (
        <FormattedMessage {...CALL_DIRECTION_MESSAGES[call.direction as keyof typeof CALL_DIRECTION_MESSAGES]} />
    ),
    segmentType: (call: APICall) => {
        return <FormattedMessage {...CALL_SEGMENT_TYPE_MESSAGES[call.segmentType]} />;
    },
    callTime: (call: APICall) => timeGenerator(call, "callTime"),
    connectTime: (call: APICall) => timeGenerator(call, "connectTime"),
    disconnectTime: (call: APICall) => timeGenerator(call, "disconnectTime"),
    duration: (call: APICall) => (call.connected ? <Duration value={call.duration} /> : null),
    recordingId: recordingIdGenerator,
    disconnectReason: (call: APICall) => call.disconnectReason,
};

export const recordingOrderer = (isDescending: boolean) => (a: Call, b: Call) => {
    const first = isDescending ? a : b;
    const second = isDescending ? b : a;
    if (first.recordingId && second.recordingId) return 0;
    if (first.recordingId) return -1;
    if (second.recordingId) return 1;
    return 0;
};

export const CallListPage = ({domainId}: {domainId: number}) => {
    const [callList, setCallList] = useState<Call[]>(null);
    const [loading, setLoading] = useState(false);
    const [query] = useCallListLazyQuery();
    const {formatMessage} = useIntl();

    const onFilterSubmit = async (interval: CallsInterval, targetNumber: number, action: SubmitAction) => {
        if (action === "download") {
            await downloadRecords<Call, CSVField>(callList, csvFields, "call_records", {
                fallbackHeaderGenerator: (field: CSVField) => formatMessage(CALL_LIST_CSV_HEADER_MESSAGES[field]),
                fieldMapper: {
                    direction: (row) =>
                        formatMessage(
                            CALL_DIRECTION_MESSAGES[row.direction as keyof typeof CALL_DIRECTION_MESSAGES] || {
                                id: "call_list.directions.unknown",
                                defaultMessage: row.direction,
                            },
                        ),
                    callTime: (row) =>
                        DateTime.fromISO(row.callTime).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS),
                    remoteNumber: (row) => stripDomain(row.remoteNumber, row.localDomain),
                    duration: (row) => (row.connected ? row.duration.toString() : ""),
                },
            });
            return;
        }

        setLoading(true);

        const variables: CallListQueryVariables = {interval: getRecordListInterval(interval)};
        if (targetNumber > 0) variables.numberId = targetNumber;
        else variables.domainId = domainId;

        const res = await query({variables, fetchPolicy: "no-cache"});

        setCallList(res.data.callList.calls ?? []);

        setLoading(false);
    };

    return (
        <div>
            <Filters
                onSubmit={onFilterSubmit}
                disabled={loading}
                domainId={domainId}
                downloadEnabled={callList !== null}
            />
            {callList !== null && (
                <RecordList<Call>
                    records={callList}
                    loading={loading}
                    style={{marginTop: 10}}
                    fields={[
                        {
                            name: "localNumber",
                            label: defineMessage({id: "call_list.localNumber", defaultMessage: "Local number"}),
                        },
                        {
                            name: "remoteNumber",
                            label: defineMessage({id: "call_list.remoteNumber", defaultMessage: "Remote number"}),
                        },
                        {
                            name: "direction",
                            label: defineMessage({id: "call_list.direction", defaultMessage: "Direction"}),
                        },
                        {
                            name: "segmentType",
                            label: defineMessage({id: "call_list.segmentType", defaultMessage: "Segment type"}),
                        },
                        {name: "callTime", label: defineMessage({id: "call_list.callTime", defaultMessage: "Time"})},
                        {
                            name: "duration",
                            label: defineMessage({id: "call_list.duration", defaultMessage: "Duration"}),
                        },
                    ]}
                    generators={fieldContentGenerators as Record<keyof Call, (call: Call) => any>}
                />
            )}
        </div>
    );
};
